import { useEffect, useState, useCallback, lazy, Suspense } from "react"
import throttle from "lodash/throttle"
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom"
import Context from "@/context/Context"
import RouteAnalytics from "./utils/telemetry/RouteAnalytics"

const ScrollTop = lazy(() => import("@/components/common/ScrollTop"))

const HomePage = lazy(() => import("@/pages/HomePage"))
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"))
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"))
const OtpVerifyPage = lazy(() => import("@/pages/auth/OtpVerifyPage"))
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"))
const ProfilePage = lazy(() => import("@/pages/account/ProfilePage"))
const BalancePage = lazy(() => import("@/pages/account/BalancePage"))
const WithdrawPage = lazy(() => import("@/pages/account/WithdrawPage"))
const BlogPage = lazy(() => import("@/pages/blog/BlogPage"))
const BlogFilterPage = lazy(() => import("@/pages/blog/BlogFilterPage"))
const BlogDetailPage = lazy(() => import("@/pages/blog/BlogDetailPage"))
const CartPage = lazy(() => import("@/pages/cart/CartPage"))
const CategoryPage = lazy(() => import("@/pages/category/CategoryPage"))
const ChatPage = lazy(() => import("@/pages/chat/ChatPage"))
const OrderPage = lazy(() => import("@/pages/order/OrderPage"))
const OrderDetailPage = lazy(() => import("@/pages/order/OrderDetailPage"))
const ProductPage = lazy(() => import("@/pages/products/ProductPage"))
const ProductsPage = lazy(() => import("@/pages/products/ProductsPage"))
const ProductRecommendationsPage = lazy(() =>
  import("@/pages/products/ProductRecommendationsPage")
)
const ProductReviewPage = lazy(() =>
  import("@/pages/products/ProductReviewPage")
)
const BestSellingProductsPage = lazy(() =>
  import("@/pages/products/BestSellingProductsPage")
)
const SearchPage = lazy(() => import("@/pages/search/SearchPage"))
const ShopPage = lazy(() => import("@/pages/shop/ShopPage"))
const CheckoutPage = lazy(() => import("@/pages/transaction/CheckoutPage"))
const PaymentPage = lazy(() => import("@/pages/transaction/PaymentPage"))
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"))
const AboutPage = lazy(() => import("@/pages/misc/AboutPage"))
const PartnershipPage = lazy(() => import("@/pages/misc/PartnershipPage"))
const ContactPage = lazy(() => import("@/pages/misc/ContactPage"))
const TncPage = lazy(() => import("@/pages/misc/TncPage"))
const CostPage = lazy(() => import("@/pages/misc/CostPage"))
const PrivacyPage = lazy(() => import("@/pages/misc/PrivacyPage"))

function AppContent() {
  const location = useLocation()
  const [scrollDirection, setScrollDirection] = useState("down")
  const [showPreloader, setShowPreloader] = useState(true)

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY
    const header = document.querySelector("header")
    const lastScrollY = window.lastScrollY || 0

    if (header) {
      if (currentScrollY > 100) {
        header.classList.add("header-bg")
      } else {
        header.classList.remove("header-bg")
      }

      if (currentScrollY > 250) {
        header.style.top = currentScrollY > lastScrollY ? "-185px" : "0px"
      } else {
        header.style.top = "0px"
      }
    }

    if (currentScrollY > 250) {
      const newDirection = currentScrollY > lastScrollY ? "down" : "up"
      if (newDirection !== scrollDirection) {
        setScrollDirection(newDirection)
      }
    } else if (scrollDirection !== "down") {
      setScrollDirection("down")
    }

    window.lastScrollY = currentScrollY
  }, [scrollDirection])

  useEffect(() => {
    const direction = localStorage.getItem("direction")
    if (direction) {
      const parsedDirection = JSON.parse(direction)
      document.documentElement.dir = parsedDirection.dir
      document.body.classList.add(parsedDirection.dir)
    }

    const preloader = document.getElementById("preloader")
    if (preloader) {
      setTimeout(() => {
        preloader.classList.add("disabled")
        setTimeout(() => setShowPreloader(false), 300)
      }, 100)
    } else {
      setShowPreloader(false)
    }

    const loadBootstrap = async () => {
      if (typeof window !== "undefined") {
        const bootstrap = await import("bootstrap/dist/js/bootstrap.esm")
        window.bootstrapModule = bootstrap
      }
    }
    loadBootstrap()

    const throttledHandleScroll = throttle(handleScroll, 100)
    window.addEventListener("scroll", throttledHandleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll)
      throttledHandleScroll.cancel()
    }
  }, [handleScroll])

  useEffect(() => {
    const cleanup = async () => {
      const bootstrap = await import("bootstrap")

      const modalElements = document.querySelectorAll(".modal.show")
      const offcanvasElements = document.querySelectorAll(".offcanvas.show")

      modalElements.forEach((modal) => {
        const modalInstance = bootstrap.Modal.getInstance(modal)
        if (modalInstance) modalInstance.hide()
      })

      offcanvasElements.forEach((offcanvas) => {
        const offcanvasInstance = bootstrap.Offcanvas.getInstance(offcanvas)
        if (offcanvasInstance) offcanvasInstance.hide()
      })
    }

    cleanup()
  }, [location.pathname])

  return (
    <div className="preload-wrapper">
      <Context>
        <div id="wrapper">
          <Suspense
            fallback={
              <div className="preload preload-container">
                <div className="preload-logo">
                  <div className="spinner"></div>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<HomePage />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/otp-verify" element={<OtpVerifyPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/balance" element={<BalancePage />} />
              <Route path="/withdraw" element={<WithdrawPage />} />

              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/category/:slug" element={<BlogFilterPage />} />
              <Route path="/blog/tag/:name" element={<BlogFilterPage />} />
              <Route path="/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/category" element={<CategoryPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route
                path="/category/:slug/:subSlug"
                element={<CategoryPage />}
              />
              <Route
                path="/category/:slug/:subSlug/:subSubSlug"
                element={<CategoryPage />}
              />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/order" element={<OrderPage />} />
              <Route path="/order/:id" element={<OrderDetailPage />} />
              <Route path="/product/:slug" element={<ProductPage />} />
              <Route
                path="/product/:slug/review"
                element={<ProductReviewPage />}
              />
              <Route path="/products" element={<ProductsPage />} />
              <Route
                path="/products/recommendations"
                element={<ProductRecommendationsPage />}
              />
              <Route
                path="/products/bestselling"
                element={<BestSellingProductsPage />}
              />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/shop/:slug" element={<ShopPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/payment/:id" element={<PaymentPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/partnership" element={<PartnershipPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/tnc" element={<TncPage />} />
              <Route path="/cost" element={<CostPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </Context>

      <Suspense fallback={null}>
        <ScrollTop />
      </Suspense>
    </div>
  )
}

function App() {
  return (
    <Router>
      <RouteAnalytics />
      <AppContent />
    </Router>
  )
}

export default App
